
import { CohortBatchView, CohortIntentView, CohortTargetView } from "@/api/types";
import { cn } from "@/lib/utils";

/**
 * A cohort proposal, drawn as one card per intent.
 *
 * <h3>Why a card per intent and not one per sentence</h3>
 * <p>Reading a sentence and confirming its result are two different problems. The parser must read
 * "move all my meetings except my Monday meetings with @doris" WHOLE, or the exception clause turns
 * into a request to schedule something with Doris. But once it is read, what comes out is N
 * independent intents, and a card each is what actually happens next, drawn. It also gives
 * clarification somewhere to land: "the marketing one" names a card, and only that card moves.</p>
 *
 * <h3>What every card must say</h3>
 * <ul>
 *   <li><b>That nothing has moved.</b> The most important thing on the screen, and the easiest to
 *       lose once the list has been re-rendered two or three times.</li>
 *   <li><b>What changed</b>, not just the new total. A reply was an instruction about WHICH
 *       meetings, so the answer has to show which meetings: dropped rows are struck through, for
 *       this turn only.</li>
 *   <li><b>A way out, per intent.</b> When refining goes wrong the escape hatch is to throw one
 *       away and say it again.</li>
 * </ul>
 */
export function CohortIntentCards({
  batch,
  busyIntentId,
  onConfirm,
  onDrop,
  onAnswer,
  onOpenConversation,
}: {
  batch: CohortBatchView;
  /** The intent whose button was just pressed, so only that card's controls go quiet. */
  busyIntentId?: string | null;
  onConfirm?: (intentId: string) => void;
  onDrop?: (intentId: string) => void;
  /** A quick-reply answer to this card's question, scoped to it (intent_id). */
  onAnswer?: (intentId: string, answer: string) => void;
  onOpenConversation?: (conversationId: string) => void;
}) {
  const live = batch.intents.filter((intent) => intent.status !== "DROPPED");
  if (live.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {live.map((intent) => (
        <IntentCard
          key={intent.id}
          intent={intent}
          named={live.length > 1}
          busy={busyIntentId === intent.id}
          onConfirm={onConfirm}
          onDrop={onDrop}
          onAnswer={onAnswer}
          onOpenConversation={onOpenConversation}
        />
      ))}
    </div>
  );
}

/** What each verb is called on screen. Plain words: the user did not type an enum. */
const VERB_LABEL: Record<CohortIntentView["verb"], string> = {
  SCHEDULE: "Set up",
  RESCHEDULE: "Move",
  CANCEL: "Cancel",
  FIND_TIME: "Find a time",
};

function IntentCard({
  intent,
  named,
  busy,
  onConfirm,
  onDrop,
  onAnswer,
  onOpenConversation,
}: {
  intent: CohortIntentView;
  /** True when the sentence produced more than one card, so each has to say which it is. */
  named: boolean;
  busy: boolean;
  onConfirm?: (intentId: string) => void;
  onDrop?: (intentId: string) => void;
  onAnswer?: (intentId: string, answer: string) => void;
  onOpenConversation?: (conversationId: string) => void;
}) {
  const acted = intent.status === "ACTED";
  const needsInput = intent.status === "NEEDS_INPUT";
  // A FIND_TIME card is an ANSWER, not a proposal (D-14): it never confirms, and "nothing has
  // moved" would be a strange badge on something that was never going to move anything.
  const isResult = intent.verb === "FIND_TIME";
  // A cancel negotiates with nobody, so a confirmed one is FINISHED, not waiting. That is the whole
  // difference between the verbs and it has to be visible, or a done thread looks stalled.
  const waiting = acted && intent.conversation_count > 0;
  return (
    <div
      className={cn(
        "rounded-2xl border bg-card p-4",
        acted ? "border-brand-teal/40" : "border-brand-blue/30",
      )}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h4 className="text-sm font-semibold text-foreground">
          {VERB_LABEL[intent.verb]}
          {named && (
            <span className="ml-1.5 font-normal text-muted-foreground">#{intent.ordinal + 1}</span>
          )}
        </h4>
        <span
          className={cn(
            "shrink-0 rounded-full px-2 py-0.5 text-xs font-medium",
            acted
              ? "bg-brand-teal/10 text-brand-teal"
              : "bg-brand-yellow/20 text-brand-contrast",
          )}
        >
          {acted
            ? waiting
              ? `${intent.conversation_count} in progress`
              : "Done"
            : needsInput
              ? "Needs an answer"
              : isResult
                ? "Just an answer"
                : "Nothing has moved"}
        </span>
      </div>

      <ul className="mt-3 flex flex-col gap-1.5">
        {intent.targets.map((target) => (
          <TargetRow
            key={target.meeting_id ?? target.person_id}
            target={target}
            onOpenConversation={onOpenConversation}
          />
        ))}
        {/* Struck rather than removed, for THIS turn only. The reply was an instruction about which
            meetings, so the answer has to show which meetings left. */}
        {intent.dropped_targets.map((target) => (
          <TargetRow key={`dropped-${target.meeting_id ?? target.person_id}`} target={target} dropped />
        ))}
      </ul>

      {/* The card's own question (NEEDS_INPUT). The next typed message answers it, scoped to
          this card, so a sibling mid-refine is never rewritten by the reply. Brand blue, not
          the contrast red: a question is the card talking, not the card failing. A closed
          answer-space (the pattern question) renders its options as quick replies. */}
      {needsInput &&
        (intent.needs ?? []).map((need) => (
          <div key={need.field} className="mt-3">
            <p className="text-sm font-medium text-brand-blue">{need.question}</p>
            {(need.options ?? []).length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5">
                {need.options!.map((option) => (
                  <button
                    key={option}
                    type="button"
                    disabled={busy}
                    onClick={() => onAnswer?.(intent.id, option)}
                    className="rounded-full border border-brand-blue/40 bg-brand-blue/5 px-3 py-1 text-xs font-medium text-brand-blue transition hover:bg-brand-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {option}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}

      {/* The FIND_TIME answer (D-14, D-20): free times, and the meetings that shape them. */}
      {isResult && !needsInput && intent.find_time && (
        <FindTimeAnswer result={intent.find_time} onOpenConversation={onOpenConversation} />
      )}

      {/* An empty proposal must say which kind of empty it is. "Nothing matched" is an answer;
          a filter the server could not read is a failure, and showing it as an answer was the
          quietest bug this feature had. A card that is asking a question is doing neither, so
          the question stands alone rather than beside a verdict about the match. A FIND_TIME
          card carrying its answer is not empty at all — an empty cohort there means the
          question was about the caller's own calendar (D-20). */}
      {!acted && !needsInput && intent.targets.length === 0 && !(isResult && intent.find_time) && (
        <p className="mt-3 text-sm text-muted-foreground">
          {intent.filter_not_understood ? "Could not tell what to match" : "Nothing matched"}
        </p>
      )}

      {!acted && (
        <div className="mt-3 flex items-center gap-2">
          {intent.confirmable && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onConfirm?.(intent.id)}
              className="rounded-full border border-brand-blue/40 bg-brand-blue/5 px-3 py-1 text-xs font-medium text-brand-blue transition hover:bg-brand-blue hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              Confirm
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => onDrop?.(intent.id)}
            className="rounded-full px-3 py-1 text-xs font-medium text-muted-foreground transition hover:bg-brand-coral/10 hover:text-brand-coral disabled:cursor-not-allowed disabled:opacity-50"
          >
            Forget this one
          </button>
        </div>
      )}
    </div>
  );
}

function TargetRow({
  target,
  dropped,
  onOpenConversation,
}: {
  target: CohortTargetView;
  dropped?: boolean;
  onOpenConversation?: (conversationId: string) => void;
}) {
  return (
    <li
      className={cn(
        "flex items-baseline justify-between gap-3 text-sm",
        dropped ? "text-muted-foreground line-through" : "text-foreground",
      )}
    >
      <span className="truncate">{target.title}</span>
      <span className="flex shrink-0 items-baseline gap-2 text-xs text-muted-foreground">
        {target.starts_at && <time dateTime={target.starts_at}>{whenOf(target.starts_at)}</time>}
        {target.conversation_id && (
          <button
            type="button"
            onClick={() => onOpenConversation?.(target.conversation_id!)}
            className="font-medium text-brand-blue transition hover:underline"
          >
            View
          </button>
        )}
      </span>
    </li>
  );
}

/**
 * The FIND_TIME card's body: when the caller is free, and what already sits in the window.
 *
 * Both halves come from one server-side read of the caller's own calendar, so they cannot
 * contradict each other. A token missing `meetings:read` gets neither half, and the card says
 * which scope is missing instead of showing a week that merely looks free.
 */
function FindTimeAnswer({
  result,
  onOpenConversation,
}: {
  result: NonNullable<CohortIntentView["find_time"]>;
  onOpenConversation?: (conversationId: string) => void;
}) {
  if (result.scope_missing) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        This token cannot read your calendar. Mint one with{" "}
        <code className="text-xs">{result.scope_missing}</code> to see free times and meetings.
      </p>
    );
  }
  return (
    <div className="mt-3 flex flex-col gap-3">
      <div>
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          You are free
        </p>
        {result.free.length === 0 ? (
          <p className="mt-1 text-sm text-muted-foreground">No free time in that window</p>
        ) : (
          <ul className="mt-1 flex flex-col gap-1">
            {result.free.map((range) => (
              <li key={range.start} className="text-sm text-foreground">
                <time dateTime={range.start}>{whenOf(range.start)}</time>
                {" to "}
                <time dateTime={range.end}>{timeOf(range.end)}</time>
              </li>
            ))}
          </ul>
        )}
      </div>
      {result.meetings.length > 0 && (
        <div>
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Already booked
          </p>
          <ul className="mt-1 flex flex-col gap-1.5">
            {result.meetings.map((meeting) => (
              <TargetRow
                key={meeting.meeting_id ?? meeting.person_id}
                target={meeting}
                onOpenConversation={onOpenConversation}
              />
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Just the clock time, for the closing bound of a range that already named its day. */
function timeOf(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** Short and local: a day, a date and a time, in the reader's own zone. */
function whenOf(iso: string): string {
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return "";
  return when.toLocaleString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}
